import { Box, Checkbox, FormControlLabel, styled } from "@mui/material";
import { useAppSelector } from "app/hooks";
import { MediaThumbnail } from "models/MediaThumbnail";
import { Verification } from "models/Verification";
import InfoIcon from "@mui/icons-material/Info";
import PlayCircle from "@mui/icons-material/PlayCircle";
import { useMemo } from "react";
import { selectUserRole } from "features/auth/authSlice";
import { UserRole } from "features/auth/types/user";
import { useExcludePhotosFromPdfMutation } from "app/api/checklists";
import { useChecklistContext } from "context/ChecklistContext";

export type ThumbnailMedia = MediaThumbnail & Verification;

type Props = {
  data: ThumbnailMedia;
  onClick?: () => void;
};

const ThumbnailWrapper = styled(Box)({
  position: "relative",
  aspectRatio: "1/1",
  overflow: "hidden",
  cursor: "pointer",
  transition: "all 0.2s ease-in-out",
  borderRadius: "12px",
  "&:hover": {
    boxShadow: "0 0 6px rgba(0, 0, 0, 0.3)",
    filter: "brightness(1.1) saturate(0.9) contrast(0.9)",
  },
});

const Image = styled("img")({
  position: "absolute",
  width: "100%",
  height: "100%",
  objectFit: "cover",
});

const Video = styled(Box)({
  position: "absolute",
  inset: 0,
  backgroundColor: "#0006",
});

const PlayButton = styled(PlayCircle)({
  position: "absolute",
  width: "100%",
  height: "100%",
  backgroundColor: "#fff6",
  padding: "30%",
  color: "#fff",
});

const Thumbnail: React.FC<Props> = ({ data, onClick }) => {
  const { checklist } = useChecklistContext();
  const userRole = useAppSelector(selectUserRole);
  const [excludePhotosFromPdf] = useExcludePhotosFromPdfMutation();

  const userCanUpload = useMemo(() => {
    if (!userRole) return false;
    return [UserRole.ADMIN, UserRole.USER_ADMIN, UserRole.SALES].includes(userRole);
  }, [userRole]);

  const isImage = data.type === 1;
  const isVideo = data.type === 2;
  const isVideoWithThumbnail = isVideo && data.mediaType === "image/png";

  const toggleExclude = () => {
    excludePhotosFromPdf({
      ids: [data.id],
      excluded: !data.excludedFromPdf,
      checklistId: checklist.id,
    });
  };

  return (
    <Box>
      <ThumbnailWrapper onClick={onClick} sx={{ opacity: data.excludedFromPdf ? 0.4 : 1 }}>
        {(isImage || isVideoWithThumbnail) && <Image src={data.imgUrl} />}

        {isVideo && (
          <Video>
            <PlayButton />
          </Video>
        )}
      </ThumbnailWrapper>

      {isVideo ? (
        <FormControlLabel
          control={<Checkbox disabled icon={<InfoIcon />} />}
          label="Film visas inte i slutrapport"
        />
      ) : (
        <FormControlLabel
          control={
            <Checkbox
              disabled={!userCanUpload}
              color="success"
              checked={!data.excludedFromPdf}
              onChange={toggleExclude}
            />
          }
          label={data.excludedFromPdf ? "Exkluderad från slutrapport" : "Inkluderad i slutrapport"}
        />
      )}
    </Box>
  );
};

export default Thumbnail;
